import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue";
import { computed, onUnmounted, ref, watch } from "vue";
import { medicalNecessityLetterService } from "@/services/medicalNecessityLetterService";
import { usePolling } from "@/composables/usePolling";
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const SAVE_DEBOUNCE_MS = 1000;
export default /*@__PURE__*/ _defineComponent({
    __name: 'MedicalNecessityLetterDialog',
    props: {
        modelValue: { type: Boolean },
        orderKey: {},
        initialDraft: {}
    },
    emits: ["update:modelValue", "attached", "state-changed"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const draftText = ref(props.initialDraft || "");
        const generating = ref(false);
        const attaching = ref(false);
        const errorMsg = ref("");
        const autoOpenPending = ref(false);
        let activeTaskId = null;
        let startedAt = 0;
        let saveTimer = null;
        const hasDraft = computed(() => draftText.value.trim().length > 0);
        const errorText = (e) => (e instanceof Error ? e.message : String(e));
        watch([generating, hasDraft], () => emit("state-changed", { generating: generating.value, hasDraft: hasDraft.value }), {
            immediate: true,
        });
        const clearPendingSave = () => {
            if (saveTimer)
                clearTimeout(saveTimer);
            saveTimer = null;
        };
        function settle() {
            activeTaskId = null;
            generating.value = false;
            if (!autoOpenPending.value)
                return;
            autoOpenPending.value = false;
            emit("update:modelValue", true);
        }
        async function pollDraft() {
            if (!activeTaskId)
                return;
            const taskId = activeTaskId;
            try {
                const result = await medicalNecessityLetterService.getDraft(props.orderKey, taskId);
                if (activeTaskId !== taskId)
                    return;
                if (result.status === "done") {
                    draftText.value = result.draftText || "";
                    settle();
                    return;
                }
                if (result.status === "error") {
                    errorMsg.value = result.error || "Letter drafting failed. Please try again.";
                    settle();
                    return;
                }
                if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                    errorMsg.value = "Letter drafting timed out. Please try again.";
                    settle();
                }
            }
            catch (e) {
                if (activeTaskId !== taskId)
                    return;
                errorMsg.value = `Failed to fetch the letter draft: ${errorText(e)}`;
                settle();
            }
        }
        const { refresh } = usePolling(pollDraft, { interval: POLL_INTERVAL_MS });
        async function startDraft() {
            clearPendingSave();
            errorMsg.value = "";
            autoOpenPending.value = true;
            generating.value = true;
            try {
                const { taskId } = await medicalNecessityLetterService.startDraft(props.orderKey);
                activeTaskId = taskId;
                startedAt = Date.now();
                refresh();
            }
            catch (e) {
                errorMsg.value = `Failed to start the letter draft: ${errorText(e)}`;
                settle();
            }
        }
        const onEdit = (value) => {
            clearPendingSave();
            saveTimer = setTimeout(() => {
                medicalNecessityLetterService.saveDraft(props.orderKey, value).catch((e) => {
                    errorMsg.value = `Failed to save the draft: ${errorText(e)}`;
                });
            }, SAVE_DEBOUNCE_MS);
        };
        async function onAttach() {
            clearPendingSave();
            errorMsg.value = "";
            attaching.value = true;
            try {
                const { link, removedLinks } = await medicalNecessityLetterService.attachDocument(props.orderKey, draftText.value);
                emit("attached", { link, removedLinks: removedLinks || [] });
                emit("update:modelValue", false);
            }
            catch (e) {
                errorMsg.value = `Failed to attach the letter: ${errorText(e)}`;
            }
            finally {
                attaching.value = false;
            }
        }
        function onDialogToggle(value) {
            emit("update:modelValue", value);
        }
        onUnmounted(clearPendingSave);
        __expose({ startDraft });
        return (_ctx, _cache) => {
            const _component_v_spacer = _resolveComponent("v-spacer");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_textarea = _resolveComponent("v-textarea");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_card_actions = _resolveComponent("v-card-actions");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_dialog = _resolveComponent("v-dialog");
            return (_openBlock(), _createBlock(_component_v_dialog, {
                "model-value": __props.modelValue,
                "max-width": "900",
                scrollable: "",
                "onUpdate:modelValue": onDialogToggle
            }, {
                default: _withCtx(() => [
                    _createVNode(_component_v_card, null, {
                        default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "d-flex align-center ga-3 py-4" }, {
                                default: _withCtx(() => [
                                    _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-h6" }, "Letter of Medical Necessity", -1)),
                                    _createVNode(_component_v_spacer),
                                    _createVNode(_component_v_btn, {
                                        variant: "tonal",
                                        color: "primary",
                                        size: "small",
                                        "prepend-icon": "mdi-refresh",
                                        loading: generating.value,
                                        disabled: attaching.value,
                                        onClick: startDraft
                                    }, {
                                        default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(hasDraft.value ? "Regenerate" : "Generate"), 1)
                                        ]),
                                        _: 1
                                    }, 8, ["loading", "disabled"]),
                                    _createVNode(_component_v_btn, {
                                        icon: "mdi-close",
                                        variant: "text",
                                        size: "small",
                                        "aria-label": "Close letter",
                                        disabled: attaching.value,
                                        onClick: _cache[0] || (_cache[0] = ($event) => (emit('update:modelValue', false)))
                                    }, null, 8, ["disabled"])
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_text, null, {
                                default: _withCtx(() => [
                                    _cache[5] || (_cache[5] = _createElementVNode("div", { class: "text-body-2 text-medium-emphasis mb-4" }, " Drafted from this submission and its supporting documents, for the prescriber to review and sign. Edit the text as needed, then attach it as the Statement of Medical Necessity to download it or send it with the submission. ", -1)),
                                    (errorMsg.value)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 0,
                                            type: "error",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mb-4"
                                        }, {
                                            default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(errorMsg.value), 1)
                                            ]),
                                            _: 1
                                        }))
                                        : _createCommentVNode("", true),
                                    (generating.value)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 1,
                                            type: "info",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mb-4"
                                        }, {
                                            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                                    _createTextVNode(" Drafting the letter in the background — you can close this and keep working; it reopens when the draft is ready. ", -1)
                                                ]))]),
                                            _: 1
                                        }))
                                        : _createCommentVNode("", true),
                                    _createVNode(_component_v_textarea, {
                                        modelValue: draftText.value,
                                        "onUpdate:modelValue": [
                                            _cache[1] || (_cache[1] = ($event) => ((draftText).value = $event)),
                                            onEdit
                                        ],
                                        label: "Letter",
                                        "auto-grow": "",
                                        rows: "18",
                                        disabled: generating.value
                                    }, null, 8, ["modelValue", "disabled"])
                                ]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_actions, { class: "justify-end" }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_btn, {
                                        variant: "text",
                                        disabled: attaching.value,
                                        onClick: _cache[2] || (_cache[2] = ($event) => (emit('update:modelValue', false)))
                                    }, {
                                        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                                _createTextVNode("Close", -1)
                                            ]))]),
                                        _: 1
                                    }, 8, ["disabled"]),
                                    _createVNode(_component_v_btn, {
                                        color: "primary",
                                        variant: "flat",
                                        loading: attaching.value,
                                        disabled: !hasDraft.value || generating.value,
                                        onClick: onAttach
                                    }, {
                                        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                                _createTextVNode(" Attach as Statement of Medical Necessity ", -1)
                                            ]))]),
                                        _: 1
                                    }, 8, ["loading", "disabled"])
                                ]),
                                _: 1
                            })
                        ]),
                        _: 1
                    })
                ]),
                _: 1
            }, 8, ["model-value"]));
        };
    }
});
